<template>
  <div class="page">
    <div class="heading"><div><h1>AI 伙伴</h1><p>为唯一的 AI 伙伴设置名字、性格与交流方式。</p></div><el-tag effect="plain" type="info">单角色模式</el-tag></div>
    <div class="notice">已接入 Qwen3.8 对话、长期记忆及游戏内 AI 伙伴。当前支持服务器受控开启的附近作物浇水，其余农活和生活行为仍在开发；实际执行情况以 SMAPI 回执为准。</div>
    <AiWorldSnapshotPanel />
    <div class="panel">
      <h3>伙伴资料</h3>
      <el-form class="form" label-position="top" @submit.prevent="save">
        <el-form-item label="伙伴名字"><el-input v-model="data.displayName" maxlength="40" show-word-limit placeholder="给她起个名字" /></el-form-item>
        <el-form-item label="性格设定"><el-input v-model="data.persona" type="textarea" :rows="5" maxlength="4000" show-word-limit placeholder="例如：有自己的生活目标，温和但并非事事顺从，重视与玩家共同经历的事情" /></el-form-item>
        <el-form-item label="说话习惯"><el-input v-model="data.speakingStyle" type="textarea" :rows="3" maxlength="2000" show-word-limit placeholder="希望她怎样说话、使用怎样的口吻" /></el-form-item>
        <div class="personality-section">
          <el-collapse v-model="traitsExpanded">
            <el-collapse-item name="personality">
              <template #title><span class="traits-title">调整性格倾向</span><span class="inline-note">7 项可选设置，默认均衡</span></template>
              <p class="hint">拖动选项调整角色的整体性格；这些偏好会影响聊天措辞和想法，但不会强迫她每次都做同样的决定。</p>
              <div class="traits-grid">
                <div v-for="trait in personalityTraits" :key="trait.key" class="trait-item">
                  <div class="trait-heading"><strong>{{ trait.label }}</strong><span>{{trait.states[data[trait.key]-1]}}</span></div>
                  <el-slider v-model="data[trait.key]" :min="1" :max="5" :step="1" :show-stops="true" :show-tooltip="false" />
                  <div class="trait-ends"><span>{{ trait.low }}</span><span>{{ trait.high }}</span></div>
                </div>
              </div>
            </el-collapse-item>
          </el-collapse>
        </div>
        <el-form-item label="允许主动聊天"><el-switch v-model="data.proactiveChat" /><span class="inline-note">后续接入 AI 运行引擎时使用</span></el-form-item>
        <el-divider />
        <h3>记忆策略与行为规则</h3>
        <p class="hint">现在支持保存玩家自述、管理长期记忆和制定计划。真实农场行为目前仅实现有限次数的附近作物浇水；计划不等于完成。</p>
        <el-form-item label="角色的全局背景说明（对所有玩家生效）"><el-input v-model="data.memoryNotes" type="textarea" :rows="4" maxlength="8000" show-word-limit placeholder="填写角色已经确定的背景。特定玩家的喜好和经历请在下方长期记忆中分别管理" /></el-form-item>
        <el-form-item label="记忆策略"><el-select v-model="data.memoryStrategy" :options="memoryOptions" style="width:100%" /></el-form-item>
        <el-form-item label="生活和互动行为规则"><el-input v-model="data.behaviorRules" type="textarea" :rows="4" maxlength="4000" show-word-limit placeholder="例如：早晨照料作物，有空去镇上，有自己的安排..." /></el-form-item>
        <el-divider />
        <h3>模型配置</h3>
        <p class="hint">模型名称可以在这里指定。真实连接由服务器安全管理，无需在浏览器中填写显卡服务器 IP。</p>
        <el-form-item label="模型名称"><el-input v-model="data.modelName" maxlength="100" placeholder="qwen3.8:27b" /></el-form-item>
        <div class="hint">模型服务器地址由后台安全通道统一配置，不需要在网页填写。</div>
        <el-button type="primary" :loading="saving" @click="save">保存伙伴配置</el-button>
      </el-form>
    </div>
    <div class="panel chat-panel">
      <div class="chat-heading">
        <div>
          <h3>和伙伴试聊</h3>
          <p class="hint">先体验角色的性格与记忆。聊天不会自动触发浇水、购物等游戏操作。</p>
        </div>
        <el-button size="small" :loading="checking" @click="checkModel">检查模型连接</el-button>
      </div>
      <div class="connection-row">
        <el-tag :type="modelStatus?.reachable?'success':'warning'">{{modelStatus?.reachable?'Qwen 已连接':modelStatus?.configured?'模型暂不可用':'等待连接模型'}}</el-tag>
        <span class="hint">{{modelStatus?.message||'正在检测模型状态…'}}</span>
      </div>
      <div ref="chatFeed" class="chat-history" role="log" aria-label="与 AI 伙伴的对话" aria-live="polite">
        <div v-if="!messages.length" class="chat-empty">🌱 还没有聊天记录。模型连接成功后，你可以先向她打个招呼。</div>
        <div v-for="(item,i) in messages" :key="item.id||i" class="chat-line" :class="item.speaker==='PLAYER'?'from-player':'from-ai'">
          <span class="chat-author">{{item.speaker==='PLAYER'?'你':data.displayName||'AI 伙伴'}}</span>
          <div class="chat-bubble">{{item.content}}</div>
        </div>
      </div>
      <div class="chat-compose">
        <el-input v-model="draft" type="textarea" :rows="3" :maxlength="2000" show-word-limit
          placeholder="例如：今天想去钓鱼吗？（Enter 发送，Shift+Enter 换行）"
          :disabled="sending||!modelStatus?.reachable" @keydown="chatKeydown"/>
        <div class="chat-actions">
          <span class="hint">{{sending?'正在组织回复…':'聊天会记录到服务器，重新进入页面也能继续。'}}</span>
          <el-button type="primary" :loading="sending" :disabled="!modelStatus?.reachable||!draft.trim()" @click="sendMessage">发送消息</el-button>
        </div>
      </div>
    </div>
    <AiMemoryPanel ref="memoryPanel" />
    <AiTaskPanel />
    <AiGameChatPanel />
  </div>
</template>
<script setup>
import {reactive,ref,nextTick,onMounted} from 'vue'
import {ElMessage} from 'element-plus'
import {http} from '../plugins/axios'
import AiMemoryPanel from '../components/AiMemoryPanel.vue'
import AiTaskPanel from '../components/AiTaskPanel.vue'
import AiGameChatPanel from '../components/AiGameChatPanel.vue'
import AiWorldSnapshotPanel from '../components/AiWorldSnapshotPanel.vue'
import {authErrorMessage} from '../auth'
const personalityTraits=[
 {key:'extraversion',label:'外向程度',low:'安静内向',high:'热情外向',states:['很内向','偏内向','均衡','偏外向','很外向']},
 {key:'initiative',label:'主动性',low:'等待邀请',high:'主动提议',states:['很少主动','偶尔主动','适度主动','比较主动','常主动提议']},
 {key:'independence',label:'独立性',low:'重视协作',high:'有自己主见',states:['喜欢商量','偏好合作','均衡','比较独立','很有主见']},
 {key:'emotionalExpression',label:'情绪表达',low:'克制平静',high:'情绪鲜明',states:['很克制','比较内敛','自然表达','比较外露','情感丰富']},
 {key:'workEthic',label:'工作习惯',low:'随性自在',high:'勤劳负责',states:['随性','较随性','劳逸均衡','认真负责','特别勤劳']},
 {key:'adventure',label:'冒险倾向',low:'偏爱稳妥',high:'热爱探险',states:['很谨慎','偏稳妥','均衡','喜欢探索','热爱冒险']},
 {key:'sociality',label:'社交倾向',low:'喜欢独处',high:'喜欢结伴',states:['喜欢独处','偏安静','均衡','喜欢交往','热爱社交']}
]
const memoryOptions=[{label:'平衡记忆（推荐）',value:'balanced'},{label:'优先长期关系',value:'longterm'},{label:'主要关注当前对话',value:'session'}]
const data=reactive({memoryNotes:'',behaviorRules:'',memoryStrategy:'balanced',displayName:'星露谷伙伴',persona:'',speakingStyle:'',modelName:'',modelEndpoint:'',proactiveChat:true,enabled:false,extraversion:3,initiative:3,independence:3,emotionalExpression:3,workEthic:3,adventure:3,sociality:3})
const saving=ref(false),checking=ref(false),modelStatus=ref(null),sending=ref(false),draft=ref(''),messages=ref([]),chatFeed=ref(null),memoryPanel=ref(null)
const traitsExpanded=ref([])
onMounted(async()=>{
 try{Object.assign(data,(await http.get('/api/manage/ai')).data)}
 catch(e){ElMessage.error(authErrorMessage(e,'伙伴资料读取失败'))}
 await Promise.all([checkModel(),loadHistory()])
})
async function checkModel(){
 checking.value=true
 try{modelStatus.value=(await http.get('/api/manage/ai/dialogue/model-status',{timeout:12000})).data}
 catch(e){modelStatus.value={configured:false,reachable:false,message:'暂时无法获取连接状态'};ElMessage.error(authErrorMessage(e,'连接检查失败'))}
 finally{checking.value=false}
}
async function loadHistory(){
 try{messages.value=(await http.get('/api/manage/ai/dialogue/history')).data||[];await scrollChat()}
 catch(e){ElMessage.error(authErrorMessage(e,'无法获取聊天记录'))}
}
async function scrollChat(){
 await nextTick()
 if(chatFeed.value)chatFeed.value.scrollTop=chatFeed.value.scrollHeight
}
function chatKeydown(event){
 if(event.key==='Enter'&&!event.shiftKey&&!event.isComposing){
  event.preventDefault();sendMessage()
 }
}
async function sendMessage(){
 if(sending.value||!draft.value.trim()||!modelStatus.value?.reachable)return
 const message=draft.value.trim()
 sending.value=true
 try{
  const {data:result}=await http.post('/api/manage/ai/dialogue/chat',{message},{timeout:120000})
  messages.value.push({speaker:'PLAYER',content:message},{speaker:'COMPANION',content:result.reply})
  draft.value=''
  await scrollChat()
  await memoryPanel.value?.refresh?.()
 }catch(e){ElMessage.error(authErrorMessage(e,'暂时没有收到回复，请稍后重试'))}
 finally{sending.value=false}
}
async function save(){
  if(saving.value)return
  if(!data.displayName.trim()){ElMessage.warning('请输入伙伴名字');return}
  saving.value=true
  try{const result=await http.put('/api/manage/ai',{...data,enabled:false});ElMessage.success(result.data.message)}
  catch(e){ElMessage.error(authErrorMessage(e,'保存失败'))}
  finally{saving.value=false}
}
</script>
<style scoped>
.page{max-width:850px}.heading{display:flex;align-items:center;justify-content:space-between;gap:15px;margin-bottom:21px}h1{font-size:27px;margin:0}h3{font-size:17px;margin:0}.heading p,.hint,.inline-note{font-size:13px;color:#718078;line-height:1.8}.notice{padding:15px 18px;background:#f1f6f2;border-radius:10px;font-size:13px;color:#52705c;line-height:1.8;margin-bottom:20px}.panel{padding:27px;border:1px solid #e5ece7;border-radius:14px;background:#fff}.form{margin-top:23px}.inline-note{margin-left:12px}.hint{margin-bottom:19px}@media(max-width:560px){.panel{padding:18px}.heading{flex-wrap:wrap}}
.chat-panel{margin-top:20px}.chat-heading{display:flex;justify-content:space-between;align-items:flex-start;gap:12px}.chat-heading h3{margin-top:0}.connection-row{display:flex;gap:12px;align-items:center;flex-wrap:wrap;margin:12px 0 18px}.connection-row .hint{margin:0}.chat-history{height:330px;overflow-y:auto;background:#f7f9f2;border:1px solid #e5ecde;border-radius:12px;padding:18px;display:flex;flex-direction:column;gap:14px}.chat-empty{margin:auto;text-align:center;font-size:13px;line-height:2;color:#839285}.chat-line{display:flex;flex-direction:column;gap:5px;max-width:86%;align-items:flex-start}.chat-line.from-player{align-self:flex-end;align-items:flex-end}.chat-author{font-size:11px;color:#799080}.chat-bubble{padding:12px 15px;border-radius:12px;background:#fffefa;color:#385342;font-size:13px;line-height:1.85;white-space:pre-wrap;overflow-wrap:anywhere;border:1px solid #e1ebdd}.from-player .chat-bubble{background:#e5f2df;border-color:#d9e7d2}.chat-compose{margin-top:15px}.chat-actions{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-top:12px}.chat-actions .hint{margin:0}</style>

<style scoped>
.personality-section{margin:10px 0 16px}.traits-title{font-weight:600;font-size:14px;color:#44664c}.traits-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));column-gap:30px;row-gap:10px;padding:8px 8px 15px}.trait-item{min-width:0}.trait-heading{display:flex;justify-content:space-between;gap:12px;font-size:13px;color:#526c59}.trait-heading span{color:#648a6b}.trait-ends{display:flex;justify-content:space-between;font-size:11px;color:#839587}.personality-section :deep(.el-slider){padding:0 8px;margin:8px 0}.personality-section :deep(.el-collapse-item__header){line-height:1.6;min-height:48px;gap:8px}.personality-section :deep(.el-collapse-item__content){padding-bottom:5px}@media(max-width:560px){.traits-grid{grid-template-columns:1fr}}
</style>
